import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, XCircle, Clock, Building, Mail, Shield, UserPlus, Lock } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { PasswordStrengthIndicator } from '@/components/PasswordStrengthIndicator';
import { validatePassword } from '@/utils/passwordPolicy';

interface InvitationData {
  id: string;
  workspace_id: string;
  email: string;
  role: string;
  status: string;
  expires_at: string;
  workspace: {
    name: string;
    organization_id?: string;
  };
  invited_by_profile?: {
    contact_email: string;
    company_name?: string;
  };
}

export default function AcceptInvite() {
  console.log('🚀 AcceptInvite component mounting...');
  
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token: pathToken } = useParams<{ token?: string }>();
  const { user, signUp } = useAuth();
  
  // Support both query parameter (?token=...) and path parameter (/invite/accept/token) formats
  const tokenFromQuery = searchParams.get('token');
  const token = tokenFromQuery || pathToken;
  
  // Enhanced debug logging
  console.log('🔍 AcceptInvite Debug (Enhanced):', {
    componentMounted: true,
    pathToken,
    tokenFromQuery,
    finalToken: token,
    tokenType: tokenFromQuery ? 'query-param' : pathToken ? 'path-param' : 'none',
    currentPath: window.location.pathname,
    currentSearch: window.location.search,
    fullURL: window.location.href,
    userLoggedIn: !!user,
    timestamp: new Date().toISOString()
  });
  
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsAuth, setNeedsAuth] = useState(false);
  
  // Registration form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  // Password validation
  const passwordValidation = validatePassword(password, { 
    firstName, 
    lastName, 
    email: invitation?.email 
  });

  useEffect(() => {
    console.log('🔄 AcceptInvite useEffect triggered:', { 
      token, 
      hasToken: !!token,
      pathToken,
      tokenFromQuery,
      effectTriggerTime: new Date().toISOString()
    });
    
    if (!token) {
      const errorMsg = `Invalid invitation link - no token found. Path: ${window.location.pathname}, Search: ${window.location.search}, PathToken: ${pathToken}, QueryToken: ${tokenFromQuery}`;
      console.error('❌ Token validation failed:', errorMsg);
      setError(errorMsg);
      setLoading(false);
      return;
    }

    // Validate token format (basic UUID check)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(token)) {
      const errorMsg = `Invalid invitation token format: "${token}" (Length: ${token.length})`;
      console.error('❌ Token format validation failed:', {
        token,
        tokenLength: token.length,
        regexTest: uuidRegex.test(token),
        errorMsg
      });
      setError(errorMsg);
      setLoading(false);
      return;
    }

    console.log('✅ Token validation passed, fetching invitation:', token);
    fetchInvitation();
  }, [token, pathToken, tokenFromQuery]);

  const fetchInvitation = async () => {
    console.log('🔍 fetchInvitation called with token:', token);
    console.log('📡 Network call should be: /functions/v1/workspace-validate-invite');
    
    try {
      // CRITICAL: Use workspace-validate-invite Edge Function (bypasses RLS for anonymous users)
      // NO direct table queries - this is the ONLY way to validate invitations for anonymous users
      console.log('📡 Calling workspace-validate-invite edge function...');
      const { data, error } = await supabase.functions.invoke('workspace-validate-invite', {
        body: { invite_id: token }, // Use invite_id (which is the invitation UUID)
      });

      console.log('📡 Edge function response:', { data, error, hasData: !!data, valid: data?.valid });

      if (error) {
        console.error('❌ Edge function error:', error);
        setError('This invitation link is invalid or has expired.');
        return;
      }

      if (!data?.valid) {
        const reason = data?.reason || 'INVALID';
        let errorMsg = 'This invitation link is invalid or has expired.';
        
        if (reason === 'EXPIRED') {
          errorMsg = 'This invitation has expired. Please contact the person who invited you for a new invitation.';
        } else if (reason === 'USED') {
          errorMsg = 'This invitation has already been used.';
        }
        
        console.error('❌ Invitation validation failed:', { reason, errorMsg });
        setError(errorMsg);
        return;
      }

      const invite = data.invite;
      if (!invite) {
        console.error('❌ No invitation data in response');
        setError('This invitation link is invalid or has expired.');
        return;
      }

      // Map the Edge Function response to the expected format
      const invitationData: InvitationData = {
        id: invite.id,
        workspace_id: invite.workspace?.id || '',
        email: invite.email,
        role: invite.role,
        status: 'pending', // Validated invitations are pending
        expires_at: invite.expires_at,
        workspace: {
          name: invite.workspace?.name || 'Workspace',
        },
        invited_by_profile: invite.inviter ? {
          contact_email: invite.inviter.email,
          company_name: invite.inviter.full_name || undefined,
        } : undefined,
      };

      console.log('✅ Invitation validated:', invitationData);
      setInvitation(invitationData);

      // If user is not logged in, require registration
      if (!user) {
        console.log('👤 User not logged in, requiring registration');
        setNeedsAuth(true);
      } else {
        console.log('👤 User is logged in:', user.email);
        // Verify the logged-in user matches the invited email
        if (user.email !== invitationData.email) {
          setError('You must be signed in as the invited user to accept this invitation');
          return;
        }
      }
      
    } catch (err: any) {
      console.error('💥 fetchInvitation error:', err);
      setError(err.message || 'Failed to load invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!invitation || !user) return;

    setAccepting(true);
    try {
      const { data, error } = await supabase.functions.invoke('workspace-invitations', {
        body: { 
          action: 'accept',
          token
        }
      });

      if (error) throw error;

      toast.success('Invitation accepted!', {
        description: `Welcome to ${invitation.workspace.name}`
      });

      // Redirect to workspace
      navigate(`/w/${invitation.workspace_id}/dashboard`);
      
    } catch (err: any) {
      console.error('Failed to accept invitation:', err);
      toast.error('Failed to accept invitation', {
        description: err.message || 'Please try again'
      });
    } finally {
      setAccepting(false);
    }
  };

  const handleRegister = async () => {
    if (!invitation) return;
    
    // Validate password strength
    if (!passwordValidation.isValid) {
      toast.error('Password does not meet security requirements', {
        description: passwordValidation.feedback[0] || 'Please use a stronger password'
      });
      return;
    }
    
    // Validate required acceptances
    if (!privacyAccepted) {
      toast.error('You must accept the Privacy Policy to continue');
      return;
    }
    
    if (!termsAccepted) {
      toast.error('You must accept the Terms of Service to continue');
      return;
    }
    
    setRegistering(true);
    
    try {
      // Log security event for registration attempt
      await supabase.functions.invoke('log-security-event', {
        body: {
          event_type: 'invitation_registration_attempt',
          event_details: {
            invitation_token: token,
            workspace_id: invitation.workspace_id,
            email: invitation.email,
            password_strength_score: passwordValidation.score
          }
        }
      });

      // Register user with company name (combine first/last name)
      const companyName = `${firstName} ${lastName}`.trim() || 'User';
      
      const { error } = await signUp(invitation.email, password, companyName);
      
      if (error) throw error;

      // Log privacy policy acceptance
      await supabase.from('privacy_policy_acceptances').insert({
        user_id: null, // Will be updated after email confirmation
        policy_version: 'v1.0',
        invitation_token: token,
        ip_address: null // Could be enhanced with IP detection
      });

      // Log successful registration
      await supabase.functions.invoke('log-security-event', {
        body: {
          event_type: 'invitation_registration_success',
          event_details: {
            invitation_token: token,
            workspace_id: invitation.workspace_id,
            email: invitation.email
          }
        }
      });

      toast.success('Account created successfully!', {
        description: 'Please check your email to verify your account, then return to accept the invitation.'
      });
      
    } catch (err: any) {
      console.error('Registration failed:', err);
      
      // Log failed registration attempt
      await supabase.functions.invoke('log-security-event', {
        body: {
          event_type: 'invitation_registration_failed',
          event_details: {
            invitation_token: token,
            workspace_id: invitation.workspace_id,
            email: invitation.email,
            error: err.message
          }
        }
      });
      
      toast.error('Failed to create account', {
        description: err.message || 'Please try again'
      });
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center p-8">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span>Loading invitation...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <XCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Invalid Invitation</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              variant="outline" 
              className="w-full" 
              onClick={() => navigate('/auth')}
            >
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!invitation) return null;

  // Show registration form if user is not logged in
  if (needsAuth && !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
              <UserPlus className="h-8 w-8 text-primary-foreground" />
            </div>
            <div>
              <CardTitle className="text-2xl">You're Invited to Join</CardTitle>
              <CardDescription className="text-lg font-medium text-foreground mt-2">
                {invitation.workspace.name}
              </CardDescription>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Invitation Context */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <div className="flex items-center space-x-3 text-sm">
                <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="font-medium">Invited:</span>
                <span className="text-muted-foreground">{invitation.email}</span>
              </div>
              <div className="flex items-center space-x-3 text-sm">
                <Shield className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="font-medium">Role:</span>
                <span className="text-muted-foreground capitalize">{invitation.role}</span>
              </div>
              {invitation.invited_by_profile?.contact_email && (
                <div className="flex items-center space-x-3 text-sm">
                  <Building className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="font-medium">Invited by:</span>
                  <span className="text-muted-foreground">{invitation.invited_by_profile.contact_email}</span>
                </div>
              )}
              <div className="flex items-center space-x-3 text-sm">
                <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="font-medium">Expires:</span>
                <span className="text-muted-foreground">
                  {new Date(invitation.expires_at).toLocaleDateString()}
                </span>
              </div>
            </div>

            {/* Registration Form */}
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="text-lg font-semibold">Create Your Account</h3>
                <p className="text-sm text-muted-foreground">
                  Complete the registration to join the workspace
                </p>
              </div>

              {/* Email (Read-only) */}
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={invitation.email}
                  disabled
                  className="bg-muted"
                />
              </div>

              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Enter first name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Enter last name"
                    required
                  />
                </div>
              </div>

              {/* Password with Strength Indicator */}
              <div className="space-y-2">
                <Label htmlFor="password" className="flex items-center space-x-2">
                  <Lock className="h-4 w-4" />
                  <span>Password</span>
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a strong password (minimum 12 characters)"
                  required
                />
                {password && (
                  <PasswordStrengthIndicator
                    password={password}
                    personalInfo={{
                      firstName,
                      lastName,
                      email: invitation.email
                    }}
                  />
                )}
              </div>

              {/* Privacy Policy and Terms Acceptance */}
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="privacy"
                    checked={privacyAccepted}
                    onCheckedChange={(checked) => setPrivacyAccepted(checked as boolean)}
                  />
                  <div className="text-sm leading-5">
                    <Label htmlFor="privacy" className="cursor-pointer">
                      I have read and agree to the{' '}
                      <button
                        type="button"
                        onClick={() => window.open('/privacy-policy', '_blank')}
                        className="text-primary underline hover:text-primary/80"
                      >
                        Privacy Policy
                      </button>
                    </Label>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="terms"
                    checked={termsAccepted}
                    onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
                  />
                  <div className="text-sm leading-5">
                    <Label htmlFor="terms" className="cursor-pointer">
                      I have read and agree to the{' '}
                      <button
                        type="button"
                        onClick={() => window.open('/terms-of-service', '_blank')}
                        className="text-primary underline hover:text-primary/80"
                      >
                        Terms of Service
                      </button>
                    </Label>
                  </div>
                </div>
              </div>

              {/* Registration Button */}
              <Button
                onClick={handleRegister}
                disabled={
                  registering ||
                  !firstName.trim() ||
                  !lastName.trim() ||
                  !password ||
                  !passwordValidation.isValid ||
                  !privacyAccepted ||
                  !termsAccepted
                }
                className="w-full"
                size="lg"
              >
                {registering ? 'Creating Account...' : 'Create Account & Join Workspace'}
              </Button>

              {/* Back to Login */}
              <div className="text-center">
                <Button
                  variant="link"
                  onClick={() => navigate('/auth')}
                  className="text-sm text-muted-foreground"
                >
                  Already have an account? Sign in instead
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-success to-success/60 flex items-center justify-center">
            <CheckCircle className="h-8 w-8 text-success-foreground" />
          </div>
          <div>
            <CardTitle className="text-2xl">Welcome to the Team!</CardTitle>
            <CardDescription className="text-lg font-medium text-foreground mt-2">
              {invitation.workspace.name}
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Invitation Details */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex items-center space-x-3 text-sm">
              <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="font-medium">Your Email:</span>
              <span className="text-muted-foreground">{invitation.email}</span>
            </div>
            <div className="flex items-center space-x-3 text-sm">
              <Shield className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="font-medium">Your Role:</span>
              <span className="text-muted-foreground capitalize">{invitation.role}</span>
            </div>
            {invitation.invited_by_profile?.contact_email && (
              <div className="flex items-center space-x-3 text-sm">
                <Building className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="font-medium">Invited by:</span>
                <span className="text-muted-foreground">{invitation.invited_by_profile.contact_email}</span>
              </div>
            )}
          </div>

          {/* Success Status */}
          <Alert className="border-success/20 bg-success/5">
            <CheckCircle className="h-4 w-4 text-success" />
            <AlertDescription className="text-success-foreground">
              Your account is verified and you're ready to join the workspace.
            </AlertDescription>
          </Alert>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button 
              onClick={handleAcceptInvitation}
              disabled={accepting}
              className="w-full"
              size="lg"
            >
              {accepting ? 'Joining Workspace...' : 'Join Workspace'}
            </Button>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => navigate('/auth')}
            >
              Back to Login
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}